import { gql, useQuery } from '@apollo/client';

import { PaginationDirection, TransactionSortKeys } from '../../../__generated__/globalTypes';
import { PATHS } from '../../../paths';
import { usePresetDateRange } from '../../DateRangePicker';

import {
  buildRecentTransactionsFilters,
  RECENT_TRANSACTIONS_FETCH_LIMIT,
  selectRecentTransactions,
} from './recentTransactions';
import RecentTransactionsSectionBase, { RecentTransactionFragmentDocument } from './RecentTransactionsSectionBase';

import type {
  GetRecentTransactionsQuery,
  GetRecentTransactionsQueryVariables,
} from './__generated__/RecentTransactionsSection.gql';

export const GET_RECENT_TRANSACTIONS = gql`
  query GetRecentTransactions(
    $pagination: PaginationInput!
    $filters: TransactionFilters
    $sortKey: TransactionSortKeys
  ) {
    getTransactionsForUser(pagination: $pagination, filters: $filters, sortKey: $sortKey) {
      edges {
        node {
          ...RecentTransaction
        }
      }
    }
  }
  ${RecentTransactionFragmentDocument}
`;

export interface IRecentTransactionsSectionProps {
  /** The record's merchant: the read's scope and where "View all" goes. */
  merchantId?: string | null;
  /** Scope the read to one recurring stream instead; the section then reads "Recent payments". */
  recurringTransactionId?: string | null;
  /** The page's own transaction, dropped so the list shows the other rows. */
  excludeTransactionId?: string | null;
  className?: string;
}

/**
 * `RecentTransactionsSectionBase` wired to `getTransactionsForUser`: the newest
 * rows at the record's merchant (or in its recurring stream), one more than
 * shown so dropping the page's own record leaves a full list. "View all" opens
 * the merchant page at the `allTime` preset rather than the pane's date range,
 * which the other merchant links keep; until the app-wide transactions load,
 * that preset starts at `OLDEST_TRANSACTION_DATE_FALLBACK`. A stream without a
 * merchant lists its payments unlinked.
 * Hidden without a merchant or stream, when nothing else exists there, and on
 * a failed read.
 */
const RecentTransactionsSection = ({
  merchantId,
  recurringTransactionId,
  excludeTransactionId,
  className,
}: IRecentTransactionsSectionProps) => {
  const hasScope = Boolean(merchantId || recurringTransactionId);
  const { data, loading, error } = useQuery<GetRecentTransactionsQuery, GetRecentTransactionsQueryVariables>(
    GET_RECENT_TRANSACTIONS,
    {
      variables: {
        pagination: { limit: RECENT_TRANSACTIONS_FETCH_LIMIT, direction: PaginationDirection.Forward },
        filters: buildRecentTransactionsFilters({ merchantId, recurringTransactionId }),
        sortKey: TransactionSortKeys.Date,
      },
      skip: !hasScope,
    }
  );
  const allTimeDateRange = usePresetDateRange('allTime');

  // ApolloProvider's errorLink already reports the failure; a secondary section hides rather than breaking the page.
  if (!hasScope || error) {
    return null;
  }

  const transactions = selectRecentTransactions(
    data?.getTransactionsForUser.edges.map((edge) => edge.node) ?? [],
    excludeTransactionId
  );

  return (
    <RecentTransactionsSectionBase
      title={recurringTransactionId ? 'Recent payments' : 'Recent transactions'}
      transactions={transactions}
      viewAllPath={merchantId ? PATHS.MERCHANT(merchantId, allTimeDateRange) : undefined}
      loading={loading}
      className={className}
    />
  );
};

export default RecentTransactionsSection;
